import { ComboboxItem } from '@mantine/core';
import { TEST_ELEMENT_IDS, makeComplexDataTestId } from '@dealstack/shared/tests/testElementIds';

/**
 * Build a Mantine v7 `renderOption` callback that stamps each rendered option
 * with a per-option `data-testid` suffixed by the option's label. Pass the
 * result as the `renderOption` prop on a `<Select>` / `<MultiSelect>`.
 *
 * Mantine wraps the returned node in the option element (handling selection,
 * hover, etc.), so this only renders the option content.
 *
 * Note: country listboxes prepend a "Frequently used" group that duplicates
 * several option labels (e.g. "United Kingdom" appears twice). Consumers
 * targeting such lists still need `.first()` in e2e.
 */
export const makeOptionWithTestId = (testIdBase: TEST_ELEMENT_IDS) => {
	const renderOption = ({ option }: { option: ComboboxItem }) => (
		<div
			data-testid={makeComplexDataTestId(testIdBase, option.label)}
			data-value={option.value}
		>
			{option.label}
		</div>
	);
	return renderOption;
};

/**
 * As `makeOptionWithTestId`, but suffixes the `data-testid` with the option's
 * value instead of its label. Use for listboxes whose labels are translated
 * (or vary by context), where the value is the only stable handle — e.g.
 * relation types, whose label depends on the related person's type.
 */
export const makeOptionWithTestIdByValue = (testIdBase: TEST_ELEMENT_IDS) => {
	const renderOption = ({ option }: { option: ComboboxItem }) => (
		<div data-testid={makeComplexDataTestId(testIdBase, option.value)}>{option.label}</div>
	);
	return renderOption;
};
